import React, { useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import heroImage from '@/assets/hero-team-photo.jpg';
import heroMobileImage from '@/assets/hero-mobile.jpg';

interface HeroProps {
  className?: string;
}

const Hero: React.FC<HeroProps> = ({
  className
}) => {
  const heroRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const subheadingRef = useRef<HTMLParagraphElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    // Simple animation for elements appearing
    const elements = [headingRef.current, subheadingRef.current, ctaRef.current];
    elements.forEach((el, index) => {
      if (el) {
        setTimeout(() => {
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
        }, 200 + index * 200);
      }
    });

    // Basic parallax effect on scroll
    const handleScroll = () => {
      if (heroRef.current) {
        const scrollPosition = window.scrollY;
        const parallaxElement = heroRef.current.querySelector('.parallax-bg') as HTMLElement;
        if (parallaxElement) {
          parallaxElement.style.transform = `translateY(${scrollPosition * 0.5}px)`;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return <section ref={heroRef} className={cn('relative h-screen min-h-[500px] flex items-center justify-center text-white overflow-hidden', className)}>
      {/* Background Image with Parallax Effect */}
      <div className="parallax-bg absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${isMobile ? heroMobileImage : heroImage})` }}>
        <div className="absolute inset-0 bg-black/50"></div>
      </div>
      
      {/* Content */}
      <div className="container-tight relative z-10 text-center px-4 md:px-8">
        <h1 ref={headingRef} className={cn(
          "font-headline font-black mb-4 md:mb-8 opacity-0 transform translate-y-10 transition-all duration-700 ease-out leading-none",
          isMobile ? "text-5xl" : "text-6xl md:text-8xl lg:text-9xl"
        )} style={{
        textShadow: '0 4px 12px rgba(0,0,0,0.6)'
      }}>
          <span className={cn(
            "block mb-4 uppercase tracking-wider text-white font-black", 
            isMobile ? "text-3xl" : "text-4xl md:text-6xl lg:text-7xl"
          )}>Entretiens McBain</span>
          <span className={cn(
            "block italic mb-3",
            isMobile ? "text-xl" : "text-2xl md:text-4xl lg:text-5xl" 
          )}>
            Votre Extérieur, Notre Expertise!
          </span>
        </h1>
        
        <div ref={ctaRef} className={cn(
          "flex flex-col sm:flex-row justify-center gap-4 md:gap-6 opacity-0 transform translate-y-10 transition-all duration-700 ease-out delay-400",
          isMobile ? "px-2" : ""
        )}>
          <Button href="/services" size={isMobile ? "default" : "lg"} variant="primary" className={cn(
            "text-base md:text-lg rounded-lg shadow-lg",
            isMobile ? "w-full py-3" : "px-8 py-4"
          )}>
            Découvrir nos services
          </Button>
          {!isMobile && (
            <Button href="/quote" size="lg" variant="outline" className="bg-transparent border-white text-white hover:bg-white/20 text-base md:text-lg rounded-lg shadow-lg px-8 py-4" icon={<ArrowRight size={18} />} iconPosition="right">
              Demander un devis
            </Button>
          )}
        </div>
      </div>
      
      {/* Scroll Down Indicator - Hide on mobile */}
      {!isMobile && (
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center text-white animate-bounce">
          <div className="h-12 w-0.5 bg-white/70 mb-2"></div>
          <span className="text-sm uppercase tracking-wider font-medium">Défiler</span>
        </div>
      )}
    </section>;
};

export default Hero;
